import React from 'react'
import {
  Modal,
  Button,
  Upload,
  Tooltip,
  Popconfirm,
  message
} from 'antd'
import {
  FileTextOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined,
  FileImageOutlined,
  DownloadOutlined
} from '@ant-design/icons'

import './commonAttachmentModal.scss'

const { Dragger } = Upload

/**
 * Helper function to sanitize file names (remove spaces)
 * @param {File} file - Original file object
 * @returns {File} - New file object with sanitized name
 */
const sanitizeFileName = (file) => {
  if (!file || !file.name) return file

  // Create a new file object with sanitized name (replacing spaces with underscores)
  const newName = file.name.replace(/\s+/g, '_')

  // If the name hasn't changed, return the original file
  if (newName === file.name) return file

  // Create a new File object with the sanitized name
  const sanitizedFile = new File([file], newName, { type: file.type })

  // Copy over any additional properties that might be used by antd Upload
  Object.keys(file).forEach(key => {
    if (key !== 'name') {
      sanitizedFile[key] = file[key]
    }
  })

  return sanitizedFile
}

/**
 * Helper function to get display name for attachment
 * @param {Object} doc - Document object
 * @param {number} index - Index for fallback naming
 * @returns {string} - Display name for the document
 */
const getDocumentDisplayName = (doc, index) => {
  // Priority order for getting the document name:
  // 1. doc.name (most common)
  // 2. filename from doc object
  // 3. extract filename from URL
  // 4. fallback naming

  if (doc.name && doc.name.trim() !== '') {
    return doc.name.trim()
  }

  if (doc.filename && doc.filename.trim() !== '') {
    return doc.filename.trim()
  }

  // Try to extract filename from URL
  if (doc.url) {
    try {
      const urlParts = doc.url.split('/')
      const lastPart = urlParts[urlParts.length - 1]
      // Remove query parameters
      const filename = lastPart.split('?')[0]
      if (filename && filename.includes('.')) {
        return decodeURIComponent(filename)
      }
    } catch (error) {
      console.warn('Could not extract filename from URL:', doc.url, error)
    }
  }

  // Fallback naming based on type or index
  if (doc.type === 'pdf') {
    return `Document_${index + 1}.pdf`
  } else if (doc.type === 'image') {
    return `Image_${index + 1}`
  } else {
    return `Attachment_${index + 1}`
  }
}

/**
 * Helper function to determine file type from various sources
 * @param {Object} doc - Document object
 * @returns {string} - File type ('pdf' or 'image')
 */
const getDocumentType = (doc) => {
  // Check explicit type field
  if (doc.type) {
    if (doc.type === 'application/pdf') return 'pdf'
    if (doc.type.startsWith('image/')) return 'image'
    return doc.type
  }

  // Check file extension from name
  if (doc.name) {
    const lowercaseName = doc.name.toLowerCase()
    if (lowercaseName.endsWith('.pdf')) {
      return 'pdf'
    } else if (lowercaseName.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      return 'image'
    }
  }

  // Check URL for file extension
  if (doc.url) {
    const lowercaseUrl = doc.url.toLowerCase()
    if (lowercaseUrl.includes('.pdf')) {
      return 'pdf'
    } else if (lowercaseUrl.match(/\.(jpg|jpeg|png|gif|webp)/)) {
      return 'image'
    }
  }

  // Default to image if uncertain
  return 'image'
}

/**
 * Reusable AttachmentModal Component
 * 
 * A flexible component for handling document attachments with preview, download, 
 * and upload functionality that can be customized for different use cases.
 * 
 * @param {Object} props
 * @param {boolean} props.visible - Controls modal visibility
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {string} props.title - Modal title (e.g., "Attachments for Transaction ABC")
 * @param {Array} props.existingAttachments - List of existing attachments/documents
 * @param {Array} props.fileList - List of files being uploaded (for local state)
 * @param {Function} props.setFileList - Function to update file list (for local state)
 * @param {Object} props.config - Configuration object for customizing behavior
 * @param {Object} props.handlers - Object containing handler functions
 * @param {Object} props.loadingStates - Loading states for various actions
 * @param {React.Component} props.customSpinner - Custom spinner component (optional)
 */
const CommonAttachmentModal = ({
  visible,
  onClose,
  title,
  existingAttachments = [],
  fileList = [],
  setFileList,
  config = {},
  handlers = {},
  loadingStates = {},
  customSpinner
}) => {
  // Default configuration
  const defaultConfig = {
    enableUpload: true,
    enableDownload: true,
    enablePreview: true,
    enableDelete: true,
    maxFileSize: 10, // MB
    acceptedTypes: ['image/*', '.pdf', 'application/pdf'],
    allowedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/gif',
      'image/bmp',
      'image/webp'
    ],
    uploadText: 'Click or drag files to this area to upload',
    uploadHint: 'Support for single or bulk upload. Image and PDF files are allowed. Files will be automatically saved.',
    disableModifications: false,
    disabledTooltipMessage: '',
    showOriginalFilename: true,
    multiple: true,
    requireAtLeastOneAttachment: false
  }

  // Merge default config with provided config
  const finalConfig = { ...defaultConfig, ...config }

  // Default handlers
  const defaultHandlers = {
    handleUploadFile: (file) => {
      console.log('Upload file:', file)
      return false // Prevent default upload
    },
    handlePreviewDocument: (doc) => {
      console.log('Preview document:', doc)
    },
    handleDownloadDocument: (doc) => {
      console.log('Download document:', doc)
    },
    handleDeleteDocument: (docId, attachmentId) => {
      console.log('Delete document:', docId, attachmentId)
    },
    onAttachmentsUpdate: (updatedFiles) => {
      console.log('Attachments updated:', updatedFiles)
    }
  }

  // Merge default handlers with provided handlers
  const finalHandlers = { ...defaultHandlers, ...handlers }

  // Enhanced upload handler with filename sanitization and validation
  const handleUploadWithSanitizedName = (file) => {
    // First, sanitize the filename (remove spaces)
    const sanitizedFile = sanitizeFileName(file)

    
    

    // Validate file type
    if (!finalConfig.allowedMimeTypes.includes(sanitizedFile.type)) {
      message.error('Only PDF, JPG, JPEG, PNG files are allowed!')
      return false
    }

    // Pass the sanitized file to the provided handler
    return finalHandlers.handleUploadFile(sanitizedFile)
  }

  // Handle file removal from upload list
  const handleRemoveFromFileList = (fileUid) => {
    if (setFileList) {
      const updatedFileList = fileList.filter(item => item.uid !== fileUid)
      setFileList(updatedFileList)
    }
  }

  // Get all valid attachments
  const validAttachments = existingAttachments.filter(doc => {
    return doc && (
      (doc.name && doc.name.trim() !== '') ||
      (doc.filename && doc.filename.trim() !== '') ||
      doc.url ||
      doc.attachmentId ||
      doc._id ||
      doc.uid
    )
  }) || []

  // Sort attachments to prioritize those with actual names
  validAttachments.sort((a, b) => {
    const aHasName = (a.name && a.name.trim() !== '') || (a.filename && a.filename.trim() !== '')
    const bHasName = (b.name && b.name.trim() !== '') || (b.filename && b.filename.trim() !== '')

    if (aHasName && !bHasName) return -1
    if (!aHasName && bHasName) return 1
    return 0
  })

  const hasDocuments = validAttachments.length > 0 || fileList.length > 0
  const attachmentCount = validAttachments.length + fileList.length
  const cannotDeleteLastAttachment = finalConfig.requireAtLeastOneAttachment && attachmentCount <= 1
  const deleteDisabled = finalConfig.disableModifications || cannotDeleteLastAttachment
  const deleteDisabledTooltip = finalConfig.disableModifications
    ? finalConfig.disabledTooltipMessage
    : 'At least one attachment must be present. Add another attachment before deleting this file.'

  // Custom close handler
  const handleClose = () => {
    if (finalHandlers.onAttachmentsUpdate) {
      finalHandlers.onAttachmentsUpdate(fileList)
    }
    onClose()
  }

  // Default loading component
  const LoadingComponent = customSpinner || (() => (
    <div className="upload-loading">
      <div className="spinner">
        <div className="spinner-circle"></div>
      </div>
      <p>Uploading...</p>
    </div>
  ))

  return (
    <Modal
      title={title || 'Attachments'}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          onClick={handleClose}
        >
          Close
        </Button>
      ]}
      width={700}
      className="attachment-modal"
    >
      <div>
        <div className="attachment-container">
          {/* Document List with Preview and Download buttons */}
          <div className="file-list-container">
            {hasDocuments ? (
              <>
                {/* Display existing attachments */}
                {validAttachments.map((doc, index) => {
                  const displayName = getDocumentDisplayName(doc, index)
                  const docType = getDocumentType(doc)
                  const docId = doc.id || doc.attachmentId || doc._id || doc.uid

                  return (
                    <div
                      key={docId || index}
                      className="file-list-item"
                    >
                      <div className="file-info">
                        {/* Show different icon based on file type */}
                        {docType === 'pdf' ? (
                          <FileTextOutlined className="file-icon pdf" />
                        ) : (
                          <FileImageOutlined className="file-icon image" />
                        )}
                        <span className="file-name" title={displayName}>
                          {displayName}
                          {/* Show original filename in smaller text if different from display name */}
                          {finalConfig.showOriginalFilename && doc.filename && doc.filename !== displayName && (
                            <span style={{ fontSize: '0.8em', color: '#999', display: 'block' }}>
                              Original: {doc.filename}
                            </span>
                          )}
                        </span>
                      </div>

                      <div className="file-actions">
                        {/* Preview button */}
                        {finalConfig.enablePreview && (
                          <Tooltip title="Preview">
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={() => finalHandlers.handlePreviewDocument(doc)}
                              loading={!!(loadingStates.previewAttachment && loadingStates.previewAttachment[docId])}
                            />
                          </Tooltip>
                        )}

                        {/* Download button */}
                        {finalConfig.enableDownload && (
                          <Tooltip title="Download">
                            <Button
                              type="text"
                              icon={<DownloadOutlined />}
                              onClick={() => finalHandlers.handleDownloadDocument(doc)}
                              loading={!!(loadingStates.downloadAttachment && loadingStates.downloadAttachment[docId])}
                            />
                          </Tooltip>
                        )}

                        {/* Delete button with confirmation modal */}
                        {finalConfig.enableDelete && (
                          <Popconfirm
                            title="Are you sure you want to delete this file?"
                            onConfirm={() => finalHandlers.handleDeleteDocument(docId, doc.attachmentId)}
                            okText="Yes"
                            cancelText="No"
                            disabled={deleteDisabled}
                          >
                            <Tooltip title={deleteDisabled ? deleteDisabledTooltip : 'Delete'}>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={deleteDisabled}
                                loading={!!(loadingStates.deleteAttachment && loadingStates.deleteAttachment[docId])}
                              />
                            </Tooltip>
                          </Popconfirm>
                        )}
                      </div>
                    </div>
                  )
                })}

                {/* Preview for files being uploaded */}
                {fileList.map(file => (
                  <div
                    key={file.uid}
                    className="file-list-item uploading"
                  >
                    <div className="file-info">
                      {/* Show different icon based on file type */}
                      {file.type === 'application/pdf' ? (
                        <FileTextOutlined className="file-icon pdf" />
                      ) : (
                        <FileImageOutlined className="file-icon image" />
                      )}
                      <span className="file-name" title={file.name}>
                        {file.name}
                      </span>
                    </div>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveFromFileList(file.uid)}
                      disabled={cannotDeleteLastAttachment}
                    />
                  </div>
                ))}
              </>
            ) : (
              <div className="no-documents">
                <FileTextOutlined />
                <p>No documents attached</p>
              </div>
            )}
          </div>

          {/* Upload Section */}
          {finalConfig.enableUpload && !finalConfig.disableModifications && (
            <Dragger
              name="files"
              multiple={finalConfig.multiple}
              accept={finalConfig.acceptedTypes.join(',')}
              fileList={fileList}
              showUploadList={false}
              beforeUpload={handleUploadWithSanitizedName}
              disabled={loadingStates.uploadAttachment}
              className="attachment-upload"
            >
              {loadingStates.uploadAttachment ? (
                <LoadingComponent />
              ) : (
                <>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">{finalConfig.uploadText}</p>
                  <p className="ant-upload-hint">{finalConfig.uploadHint}</p>
                </>
              )}
            </Dragger>
          )}

          {/* Message when upload is disabled */}
          {finalConfig.disableModifications && finalConfig.disabledTooltipMessage && (
            <div className="upload-disabled-message">
              <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', marginTop: '16px' }}>
                {finalConfig.disabledTooltipMessage}
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CommonAttachmentModal